import React, { useMemo, useState, useEffect } from "react";
import {
    Button,
    makeStyles,
    tokens,
    Badge,
    Tooltip,
    useToastController,
    Toast,
    ToastTitle,
    Toaster,
    Spinner,
    Text
} from "@fluentui/react-components";
import GlobalTableSearch from "./GlobalTableSearch";
import {
    Delete16Regular,
    CircleOff16Regular,
    Checkmark16Regular,
    Info16Regular,
    ArrowUp16Regular,
    ArrowDown16Regular,
    ArrowSort16Regular,
} from "@fluentui/react-icons";
import AdminTopbar from "../AdminTopbar";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";
import AdminService from "../AdminService";
import { useAdminAuth } from "../AdminAuthContext";
import { formatDateTime } from "../adminUtil";
import TablePagination from "../TablePagination";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles({
    container: {
        padding: "8px 6px",
    },
    header: {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        marginBottom: "12px",
        fontSize: "18px",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
        background: tokens.colorNeutralBackground1,

        "& th, & td": {
            padding: "4px",
            borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
            textAlign: "left",
        },

        "& thead th": {
            background: tokens.colorNeutralBackground2,
            fontWeight: 600,
        },
    },
    actions: {
        display: "flex",
        gap: "6px",
    },
    topbar: {
        gridColumn: "2",
        gridRow: "1",
        display: "flex",
        alignItems: "center",
        justifyContent: 'space-between',
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        backgroundColor: tokens.colorNeutralBackground1,
        position: "sticky",
        top: '0px',
        zIndex: 1000,
    },
    globalSearch: {
        marginRight: "24px"
    },
    tooltipWrapper: {
        padding: "8px",
    },
    row: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "8px",
        marginBottom: "4px",
    },
    activeIcon: {
        color: tokens.colorPaletteGreenForeground1,
    },
    inactiveIcon: {
        color: tokens.colorPaletteRedForeground1,
    },
    total: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        paddingBottom: "4px",
        marginBottom: "4px",
    },
    tableWrapper: {
        height: "calc(100vh - 58px)",
        display: "flex",
        flexDirection: "column",
    },
    tableScroll: {
        flex: 1,
        overflowY: "auto",
    },
    tableHeader: {
        position: "sticky",
        top: 0,
        zIndex: 2,
        background: tokens.colorNeutralBackground2,
    },
    paginationWrapper: {
        borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
        background: tokens.colorNeutralBackground1,
        position: "sticky",
        bottom: 0,
        zIndex: 2,
        padding: "0px 8px",
    },
});

const columnHelper = createColumnHelper();

export default function UsersTable({
    tenantId,
    onBack,
}) {
    const styles = useStyles();
    const toasterId = "user-toaster";
    const { dispatchToast } = useToastController(toasterId);
    const { userRoles, userInfo } = useAdminAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sorting, setSorting] = useState([]);
    const [deletingUserId, setDeletingUserId] = useState(null);
    const [deactivatingUserId, setDeactivatingUserId] = useState(null);
    const [activatingUserId, setActivatingUserId] = useState(null);
    // ── Confirmation modal targets ──────────────────────────────────────────
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deactivateTarget, setDeactivateTarget] = useState(null);
    const [activateTarget, setActivateTarget] = useState(null);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 25,
    });
    const isFetchingRef = React.useRef(false);
    const [globalFilter, setGlobalFilter] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [debouncedSearch, setDebouncedSearch] = useState(globalFilter);
    const [summary, setSummary] = useState({
        totalUsers: 0,
        activeCount: 0,
        inactiveCount: 0,
        filteredTotal: 0,
    });
    const [tenantInfo, setTenantInfo] = useState(null);
    const fetchUsers = async () => {
        if (isFetchingRef.current) return; // block duplicate
        isFetchingRef.current = true;

        setLoading(true);
        setError(null);

        try {
            const sort = sorting?.[0];

            const res = await AdminService.getUsersOfTenant(
                tenantId || undefined,
                {
                    page: pagination.pageIndex + 1,
                    pageSize: pagination.pageSize,
                    search: debouncedSearch || undefined,
                    sortBy: sort?.id,
                    sortOrder: sort?.desc ? "desc" : "asc",
                }
            );
            setTenantInfo({
                tenantId: res?.tenant?.tenantId || null,
                tenantName: res?.tenant?.tenantName || null,
            });
            setUsers(res?.data || []);
            setTotalCount(res?.pagination?.total || 0);
            setSummary(res?.summary || {
                totalUsers: 0,
                activeCount: 0,
                inactiveCount: 0,
                filteredTotal: 0,
            });
        } catch (err) {
            setError(err);
            setUsers([]);
        } finally {
            setLoading(false);
            isFetchingRef.current = false;
        }
    };
    useEffect(() => {
        fetchUsers();
    }, [tenantId, userRoles, userInfo, pagination, sorting, debouncedSearch]);

    useEffect(() => {
        // eslint-disable-next-line no-undef
        const timer = setTimeout(() => {
            setDebouncedSearch(globalFilter);
        }, 900);

        // eslint-disable-next-line no-undef
        return () => clearTimeout(timer);
    }, [globalFilter]);

    const handleDelete = async (user) => {
        setDeletingUserId(user.userId);
        try {
            await AdminService.deleteMsTeamsUser(
                user.userId,
                tenantId
            );
            await fetchUsers();
            dispatchToast(
                <Toast><ToastTitle> {`${user.userName || user.userId} is deleted successfully.`}</ToastTitle></Toast>,
                {
                    intent: "success",
                },
            );
        } catch (err) {
            setError(err);
            dispatchToast(
                <Toast><ToastTitle>{err?.message || "Failed to delete user."}</ToastTitle></Toast>,
                {
                    intent: "error",
                },
            );
        } finally {
            setDeletingUserId(null);
        }
    };

    const handleDeactivate = async (user) => {
        setDeactivatingUserId(user.userId);
        try {
            await AdminService.msUserDeactivation(
                user.userId,
                tenantId
            );
            await fetchUsers();
            dispatchToast(
                <Toast><ToastTitle>{`${user.userName || user.userId} is deactivated.`}</ToastTitle></Toast>,
                {
                    intent: "success",
                },
            );
        } catch (err) {
            setError(err);
            dispatchToast(
                <Toast><ToastTitle>{err?.message || "Failed to deactivate user."}</ToastTitle></Toast>,
                {
                    intent: "error",
                },
            );
        } finally {
            setDeactivatingUserId(null);
        }
    };

    const handleActivate = async (user) => {
        setActivatingUserId(user.userId);
        try {
            await AdminService.msUserActivation(
                user.userId,
                tenantId
            );
            await fetchUsers();
            dispatchToast(
                <Toast>  <ToastTitle> {`${user.userName || user.userId} is activated.`}</ToastTitle></Toast>,
                {
                    intent: "success",
                },
            );
        } catch (err) {
            setError(err);
            dispatchToast(
                <Toast><ToastTitle> {err?.message || "Failed to activate user."}</ToastTitle></Toast>,
                {
                    intent: "error",
                },
            );
        } finally {
            setActivatingUserId(null);
        }
    };

    const columns = useMemo(
        () => [
            columnHelper.accessor("userName", {
                header: "User Name",
                sortingFn: "alphanumeric"
            }),

            columnHelper.accessor("userId", {
                header: "User ID",
                cell: (info) => <strong>{info.getValue()}</strong>,
                sortingFn: "alphanumeric",
            }),

            columnHelper.accessor("upn", {
                header: "UPN",
                sortingFn: "alphanumeric",
            }),
            columnHelper.accessor("firstLoginDate", {
                header: "First Login (UTC)",
                cell: (info) => formatDateTime(info.getValue()),
                sortingFn: (rowA, rowB, columnId) => {
                    return new Date(rowA.getValue(columnId) || 0) -
                        new Date(rowB.getValue(columnId) || 0);
                },
            }),
            columnHelper.accessor("lastLoginDate", {
                header: "Last Login (UTC)",
                cell: (info) => formatDateTime(info.getValue()),
                sortingFn: (rowA, rowB, columnId) => {
                    return new Date(rowA.getValue(columnId) || 0) -
                        new Date(rowB.getValue(columnId) || 0);
                },
            }),
            columnHelper.accessor("isActive", {
                header: "Status",
                sortingFn: "basic",
                cell: (info) =>
                    info.getValue() ? (
                        <Badge color="success">Active</Badge>
                    ) : (
                        <Badge color="danger">Inactive</Badge>
                    ),
            }),

            columnHelper.accessor("instanceUrl", {
                header: "Instance URL",
                enableSorting: false,
                cell: (info) => {
                    const instanceUrl = info.getValue();
                    const orgId = info.row.original.sfOrgId;
                    if (!instanceUrl) return <span style={{ opacity: 0.45 }}>—</span>;
                    return (
                        <Tooltip
                            content={orgId ? `Org ID: ${orgId}` : "No Org ID"}
                            relationship="label"
                        >
                            <span style={{ fontSize: "12px", wordBreak: "break-all", cursor: "default" }}>
                                {instanceUrl}
                            </span>
                        </Tooltip>
                    );
                },
            }),
            columnHelper.display({
                id: "actions",
                header: "Actions",
                enableSorting: false,
                cell: ({ row }) => {
                    const user = row.original;
                    return (
                        <div className={styles.actions}>
                            {/* Toggle Active / Deactivate / Activate */}
                            {user.isActive ? (
                                <Tooltip content="Deactivate">
                                    <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<CircleOff16Regular />}
                                        onClick={() => setDeactivateTarget(user)}
                                        disabled={deactivatingUserId === user.userId}
                                        style={{
                                            color: "#d13438",
                                            backgroundColor: "rgba(209,52,56,0.1)",
                                        }}
                                    />
                                </Tooltip>
                            ) : (
                                <Tooltip content="Activate">
                                    <Button
                                        size="small"
                                        appearance="subtle"
                                        icon={<Checkmark16Regular />}
                                        onClick={() => setActivateTarget(user)}
                                        disabled={activatingUserId === user.userId}
                                        style={{
                                            color: "#107c10",
                                            backgroundColor: "rgba(16,124,16,0.1)",
                                        }}
                                    />
                                </Tooltip>
                            )}
                            {
                                tenantId && (<Tooltip content="Delete User">
                                    <Button
                                        size="small"
                                        appearance="transparent"
                                        icon={<Delete16Regular />}
                                        onClick={() => setDeleteTarget(user)}
                                        disabled={deletingUserId === user.userId}
                                    />
                                </Tooltip>)
                            }
                        </div>
                    );
                },
            }),
        ],
        [
            styles,
            deactivatingUserId,
            activatingUserId,
            deletingUserId,
            tenantId,
        ],
    );



    // NOTE: Search is executed server-side (see fetchUsers → debouncedSearch → API).
    // We intentionally do not register a client-side globalFilterFn / getFilteredRowModel
    // here — doing so would re-filter the already-filtered page against the visible
    // columns only, and drop rows that matched on backend-only fields such as sfOrgId
    // (which is not rendered as its own column, only surfaced in the Instance URL
    // tooltip). Keeping `globalFilter` in state is fine — it drives the search input —
    // but `manualFiltering: true` tells TanStack Table not to apply it to the data.

    const table = useReactTable({
        data: users,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onGlobalFilterChange: setGlobalFilter,
        getSortedRowModel: getSortedRowModel(),
        state: {
            globalFilter,
            sorting,
            pagination
        },
        onSortingChange: setSorting,
        onPaginationChange: setPagination,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        pageCount: Math.ceil(totalCount / pagination?.pageSize),
        enableMultiSort: false,
    });
    return (
        <>
            <Toaster toasterId={toasterId} />

            {/* ── Delete confirmation ───────────────────────────────────── */}
            <ConfirmationModal
                isOpen={!!deleteTarget}
                type="delete"
                title="Delete User"
                message={`Are you sure you want to delete "${deleteTarget?.userName || deleteTarget?.userId}"? This action cannot be undone.`}
                confirmText="Delete"
                onConfirm={() => { const u = deleteTarget; setDeleteTarget(null); handleDelete(u); }}
                onCancel={() => setDeleteTarget(null)}
                loading={deletingUserId === deleteTarget?.userId}
            />

            {/* ── Deactivate confirmation ───────────────────────────────── */}
            <ConfirmationModal
                isOpen={!!deactivateTarget}
                type="warning"
                title="Deactivate User"
                message={`Deactivate "${deactivateTarget?.userName || deactivateTarget?.userId}"? They will no longer be able to sign in.`}
                confirmText="Deactivate"
                onConfirm={() => { const u = deactivateTarget; setDeactivateTarget(null); handleDeactivate(u); }}
                onCancel={() => setDeactivateTarget(null)}
                loading={deactivatingUserId === deactivateTarget?.userId}
            />

            {/* ── Activate confirmation ─────────────────────────────────── */}
            <ConfirmationModal
                isOpen={!!activateTarget}
                type="confirm"
                title="Activate User"
                message={`Activate "${activateTarget?.userName || activateTarget?.userId}"? They will regain access to sign in.`}
                confirmText="Activate"
                onConfirm={() => { const u = activateTarget; setActivateTarget(null); handleActivate(u); }}
                onCancel={() => setActivateTarget(null)}
                loading={activatingUserId === activateTarget?.userId}
            />

            <header className={styles.topbar}>
                <AdminTopbar titleTooltip={tenantInfo?.tenantId} title={tenantInfo?.tenantId ? `Users - ${tenantInfo?.tenantName ?? tenantInfo?.tenantId}` : "Manage Users"} onBack={onBack} backTooltip="Back to Customers"
                    titleExtra={
                        <Tooltip
                            content={
                                <div className={styles.tooltipWrapper}>
                                    <div className={`${styles.row} ${styles.total}`}>
                                        <Text weight="semibold">Total Users</Text>
                                        <Text size={200}>{summary?.totalUsers}</Text>
                                    </div>

                                    <div className={styles.row}>
                                        <Checkmark16Regular className={styles.activeIcon} />
                                        <Text weight="semibold">Active Users</Text>
                                        <Text size={200}>{summary?.activeCount}</Text>
                                    </div>

                                    <div className={styles.row}>
                                        <CircleOff16Regular className={styles.inactiveIcon} />
                                        <Text weight="semibold">Inactive Users</Text>
                                        <Text size={200}>{summary?.inactiveCount}</Text>
                                    </div>
                                </div>
                            }
                            relationship="label"
                        >
                            {loading ? (
                                <Spinner size="tiny" />
                            ) : (
                                <Info16Regular />
                            )}
                        </Tooltip>
                    } />
                <div className={styles.globalSearch}>
                    <GlobalTableSearch
                        value={globalFilter}
                        onChange={setGlobalFilter}
                        placeholder="Search users..."
                    />
                </div>
            </header>
            <div className={styles.container}>
                <div className={styles.tableWrapper}>
                    {loading ? (
                        <div style={{ padding: "16px" }}>Loading users...</div>
                    ) : error ? (
                            <div style={{ padding: "16px", color: "red" }}>{error?.error ? error?.error : `Error loading users`}</div>
                    ) : (
                        <div className={styles.tableScroll}>
                            <table className={styles.table}>
                                <thead className={styles.tableHeader}>
                                    {table.getHeaderGroups().map((hg) => (
                                        <tr key={hg.id}>
                                            {hg.headers.map((header) => (
                                                <th
                                                    key={header.id}
                                                    onClick={(e) =>
                                                        header.column.getToggleSortingHandler()?.(e)
                                                    }
                                                    style={{
                                                        cursor: header.column.getCanSort() ? "pointer" : "default",
                                                    }}
                                                >
                                                    <span style={{ display: "inline-flex", alignItems: "center" }}>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext(),
                                                        )}

                                                        {header.column.getCanSort() && (
                                                            <Tooltip
                                                                content={
                                                                    header.column.getIsSorted()
                                                                        ? `Sorted ${header.column.getIsSorted() === "asc"
                                                                            ? "ascending"
                                                                            : "descending"
                                                                        }`
                                                                        : "Click to sort"
                                                                }
                                                                relationship="label"
                                                            >
                                                                <span
                                                                    style={{
                                                                        display: "inline-flex",
                                                                        alignItems: "center",
                                                                        marginLeft: 4,
                                                                    }}
                                                                >
                                                                    {header.column.getIsSorted() === "asc" && (
                                                                        <ArrowUp16Regular />
                                                                    )}
                                                                    {header.column.getIsSorted() === "desc" && (
                                                                        <ArrowDown16Regular />
                                                                    )}
                                                                    {!header.column.getIsSorted() && (
                                                                        <ArrowSort16Regular />
                                                                    )}
                                                                    {/* Multi-sort index */}
                                                                    {header.column.getIsSorted() && (
                                                                        <span style={{ fontSize: 10, marginLeft: 2 }}>
                                                                            {header.column.getSortIndex() + 1}
                                                                        </span>
                                                                    )}
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </span>
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                </thead>

                                <tbody>
                                    {table.getRowModel().rows.length > 0 ? (
                                        table.getRowModel().rows.map((row) => (
                                            <tr key={row.id}>
                                                {row.getVisibleCells().map((cell) => (
                                                    <td key={cell.id}>
                                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={columns.length} style={{ padding: "16px" }}>
                                                No users found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                    <div className={styles.paginationWrapper}>
                        <TablePagination
                            table={table}
                            totalCount={totalCount}
                            pagination={pagination}
                            setPagination={setPagination}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
